import { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil, Save, Package, Truck, Plane, CheckCircle, AlertCircle, ChevronDown, Check, WandSparkles, CircleAlert, Mail } from 'lucide-react';
import { updateContainer, updateCustomerDetails, updateShipperDetails, editUsers, updateThisAirline } from '../../../Api';
import { useSelector } from 'react-redux';
import { showSwal } from '../../../Helper/ShowAlert';
import { describePackageTotals } from '../../../Helper/packageTotals';
import { TRANSPORTER } from '../../../Helper/partnerLabels';

import Tooltip from "../../../components/shared/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faUser as faUserIcon,
  faTruck as faTruckIcon,
  faCheckCircle as faCheckCircleIcon,
  faPlane as faPlaneIcon,
  faPencil,
  faSave,
  faChevronDown as faChevronDownIcon,
  faChevronLeft,
  faChevronRight,
  faExclamationTriangle,
  faRotateRight,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import AnimatedLoader from "../../../motionKit/AnimatedLoader";


const containerFieldDefinitions = [
  { key: 'origin', label: 'Origin *', type: 'text', aiExtracted: true },
  { key: 'destination', label: 'Destination *', type: 'text', aiExtracted: true },
  { key: 'receiverContactDetails.name', label: 'Receiver Contact Name', type: 'text', aiExtracted: true },
  { key: 'receiverContactDetails.email', label: 'Receiver Contact Email', type: 'email', aiExtracted: true },
  { key: 'receiverContactDetails.phone', label: 'Receiver Contact Phone', type: 'text', aiExtracted: true },
  { key: 'receiverContactDetails.deliveryAddress', label: 'Delivery Address', type: 'text', aiExtracted: true },
  { key: 'net_weight', label: 'Net Weight (kg)', type: 'number', readOnly: true },
  { key: 'net_volume_weight', label: 'Net Volume Weight (kg)', type: 'number', readOnly: true },
  { key: 'chargeable_volume_weight', label: 'Chargeable Volume Weight (kg)', type: 'number', readOnly: true },
];

const packageFieldDefinitions = [
  { key: 'description', label: 'Description', type: 'text', aiExtracted: true },
  { key: 'length', label: 'Length (cm) *', type: 'number', aiExtracted: true },
  { key: 'width', label: 'Width (cm) *', type: 'number', aiExtracted: true },
  { key: 'height', label: 'Height (cm) *', type: 'number', aiExtracted: true },
  { key: 'weight', label: 'Weight (kg)', type: 'number', aiExtracted: true },
  { key: 'volume_weight', label: 'Volume Weight (kg)', type: 'number', non_editable: true },
  // { key: 'chargeable_weight', label: 'Chargeable Weight (kg)', type: 'number', non_editable: true },
  { key: 'quantity', label: 'Quantity', type: 'number', aiExtracted: true },
  { key: 'stackable', label: 'Stackable', type: 'checkbox' },
  { key: 'hazardous', label: 'Hazardous', type: 'checkbox' },
  { key: 'hsCode', label: 'Hs Code', type: 'text', aiExtracted: true },
  { key: 'countryOfManufacture', label: 'Country Of Manufacture', type: 'text', aiExtracted: true },
];

const customerFieldDefinitions = [
  { key: 'name', label: 'Name', type: 'text', aiExtracted: true },
  { key: 'email', label: 'Email', type: 'email', readOnly: true, aiExtracted: true },
  { key: 'phone', label: 'Phone', type: 'text', aiExtracted: true },
  { key: 'address', label: 'Address', type: 'text', aiExtracted: true },
];

const shipperFileDefinitions = [
  { key: "companyName", label: 'Company Name', type: "text" },
  { key: "contactPerson", label: 'Contact Person', type: "text" },
  { key: "email", label: 'Email', type: "email" },
  { key: "phone", label: 'Phone', type: "text" },
  { key: "collectionAddress", label: 'Collection Address', type: "text" },
  { key: "collectionReference", label: 'Collection Reference', type: "text" },
  { key: "collectionTimings", label: 'Collection Timings', type: "text" },
  { key: "specialCollectionRequirements", label: 'Special Collection Requirements', type: "text" },
  { key: "eori", label: 'EORI', type: "text" },
  { key: "exportLicencingNeeds", label: 'Export Licensing Needs', type: "text" },
  { key: "customsSpecialInstructions", label: 'Customs Special Instructions', type: "text" },
  { key: "representationAuthorization", label: 'Representation Authorization', type: "text" },
];

const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), obj);

const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const lastObj = keys.reduce((acc, key) => {
    if (!acc[key]) acc[key] = {};
    return acc[key];
  }, obj);
  lastObj[lastKey] = value;
};

const resolveDisplayValue = (value) =>
  typeof value === "string" ? value : value?.toString?.() ?? value;

const isValueMissing = (value) => {
  const displayValue = resolveDisplayValue(value);
  return (
    displayValue === null ||
    displayValue === undefined ||
    (typeof displayValue === "string" && displayValue.trim() === "")
  );
};

// ONE definition of "missing" for the whole inspector. The compact header used
// to count the three read-only weight fields (which are computed, never typed)
// and to ignore packages entirely, so the same shipment reported "6 missing" in
// the manual-query workspace and "9 MISSING" on the tab pill of the email
// workspace — and neither matched what the "Request N from customer" email asked
// for. Everything that shows a count now derives it from here.
export const countMissingShipmentFields = (fields = {}) => {
  const container = containerFieldDefinitions.filter(
    ({ key, readOnly }) => !readOnly && isValueMissing(getNestedValue(fields, key))
  ).length;
  const packages = (fields.packages || []).reduce(
    (acc, pkg) =>
      acc +
      packageFieldDefinitions.filter(
        ({ key, non_editable }) => !non_editable && isValueMissing(pkg[key])
      ).length,
    0
  );
  return { container, packages, total: container + packages };
};

// Denominator for the completeness ring: only fields an operator can actually
// supply, so the ring cannot disagree with the "N missing" badge beside it.
export const countFillableShipmentFields = (fields = {}) =>
  containerFieldDefinitions.filter((d) => !d.readOnly).length +
  (fields.packages || []).length *
    packageFieldDefinitions.filter((d) => !d.non_editable).length;

const AI_TOOLTIP = "Extracted by AI — please check";

const wrapWithAiTooltip = (node, enabled) =>
  enabled ? (
    <Tooltip content={AI_TOOLTIP} position="top" wrapperClassName="w-full">
      {node}
    </Tooltip>
  ) : (
    node
  );

const AiBadge = () => (
  <span
    className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-violet-100/70 px-1.5 py-px text-[9px] font-semibold uppercase tracking-[0.14em] text-violet-600 dark:bg-violet-500/15 dark:text-violet-300"
    aria-label={AI_TOOLTIP}
  >
    <span className="h-1 w-1 rounded-full bg-violet-500 dark:bg-violet-400" aria-hidden="true" />
    AI
  </span>
);

// Design motion: two crates rest on the stack while a third settles onto them.
const PackagesStackIcon = () => (
  <span aria-hidden className="relative inline-block shrink-0" style={{ width: 21, height: 18 }}>
    <span style={{ position: "absolute", bottom: 0, left: 0, width: 9, height: 8, borderRadius: 2, background: "var(--blue-soft)", border: "1px solid var(--blue-line)" }} />
    <span style={{ position: "absolute", bottom: 0, right: 0, width: 9, height: 8, borderRadius: 2, background: "var(--blue-soft)", border: "1px solid var(--blue-line)" }} />
    <span style={{ position: "absolute", bottom: 8, left: 6, width: 9, height: 8, borderRadius: 2, background: "var(--blue)", animation: "fkqw-settle 2.8s ease-in-out infinite" }} />
  </span>
);

// Per-field state chip (design "guided fields"): a filled field is Captured,
// an aiExtracted-but-empty field the AI can read from the email, and everything
// else needs the customer. Colours come from the .fk-qw token scope with hex
// fallbacks.
const getFieldState = (aiExtracted, filled) => {
  if (filled) {
    return { label: "Captured", color: "var(--emerald, #059669)", chipBg: "var(--emerald-soft, rgba(5,150,105,0.1))", Icon: Check };
  }
  if (aiExtracted) {
    return { label: "AI can find", color: "var(--violet, #7c3aed)", chipBg: "var(--violet-soft, rgba(124,58,237,0.1))", Icon: WandSparkles, placeholder: "In the email — Parse to capture" };
  }
  return { label: "Ask customer", color: "var(--amber, #d97706)", chipBg: "var(--amber-soft, rgba(217,119,6,0.11))", Icon: CircleAlert, placeholder: "Not in the email — request it" };
};

const FieldStateChip = ({ state }) => (
  <span
    className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[9.5px] font-semibold"
    style={{ background: state.chipBg, color: state.color, fontFamily: '"IBM Plex Mono", ui-monospace, monospace', letterSpacing: "0.03em" }}
  >
    <state.Icon size={10} strokeWidth={2.5} aria-hidden />
    {state.label}
  </span>
);

const renderValueOrMissing = (
  value,
  className = "min-w-0 break-words text-[13px]",
  variant = "badge"
) => {
  const displayValue = resolveDisplayValue(value);
  const isMissing = isValueMissing(value);

  if (isMissing) {
    if (variant === "plain") {
      return (
        <span className="text-[13px] font-medium text-red-500/90">
          Missing
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 text-[11.5px] font-medium italic text-slate-400 dark:text-slate-500"
        title="This field is missing"
      >
        <span
          className="inline-block h-1 w-1 rounded-full bg-red-400/80 dark:bg-red-400/70"
          aria-hidden
        />
        Not provided
      </span>
    );
  }

  return (
    <span className={`${className} block w-full`}>{displayValue}</span>
  );
};

const deepEqualIgnoreFields = (a, b, ignoredKeys = ['chargeable_weight']) => {
  const normalizeValue = (val) => typeof val === 'string' && !isNaN(val) ? parseFloat(val) : val;

  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object' || a === null || b === null) {
    return normalizeValue(a) === normalizeValue(b);
  }
  if (Array.isArray(a)) {
    return Array.isArray(b) && a.length === b.length &&
      a.every((item, idx) => deepEqualIgnoreFields(item, b[idx], ignoredKeys));
  }

  const keysA = Object.keys(a).filter(key => !ignoredKeys.includes(key));
  const keysB = Object.keys(b).filter(key => !ignoredKeys.includes(key));

  return keysA.length === keysB.length &&
    keysA.every(key => keysB.includes(key) && deepEqualIgnoreFields(a[key], b[key], ignoredKeys));
};

function ContainerDetails({
  isEditing,
  toggleEditing,
  handleFieldChange,
  handlePackageChange,
  updateContainerEntry,
  updateContainerButtonDisabled,
  generateMailForMissingFields,
  fields,
  setFields,
  openTrigger,
  compactMode = false,
  hideRequestMissingActions = false,
  parsing = false,
}) {
  const [isContainerOpen, setIsContainerOpen] = useState(false);
  useEffect(() => {
    setIsContainerOpen(true);
  }, [openTrigger]);
  const [isPackagesOpen, setIsPackagesOpen] = useState(false);

  // One-shot magnifier sweep (design motion): the same violet lens that plays
  // while the AI parses also glides once when the operator clicks "Request
  // Missing" — a small "we're on it, reading the email" flourish.
  const [sweeping, setSweeping] = useState(false);
  const playSweep = () => {
    setSweeping(true);
    setTimeout(() => setSweeping(false), 2400);
  };

  if (compactMode) {
    const totalMissing = countMissingShipmentFields(fields).total;
    return (
      <div className="qw-card">
        {/* Inline header — replaces the wasteful top-right Edit row */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-[oklch(0.3_0.02_264/0.7)]">
          <div className="flex min-w-0 items-center gap-2">
            <FontAwesomeIcon icon={faBox} className="text-[12px] text-fk-accent dark:text-blue-400" />
            <span className="text-[12.5px] font-semibold text-slate-900 dark:text-white">
              Shipment details
            </span>
            {totalMissing > 0 && !isEditing ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                <span className="h-1 w-1 rounded-full bg-amber-500" aria-hidden />
                {totalMissing} missing
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void toggleEditing()}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-all ${
              isEditing
                ? "bg-fk-accent text-white shadow-sm hover:bg-blue-600"
                : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.3_0.024_264)]"
            }`}
          >
            <FontAwesomeIcon icon={isEditing ? faSave : faPencil} className="text-[10px]" />
            {isEditing ? "Save" : "Edit"}
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-2">
            {containerFieldDefinitions.map(({ key, label, type, readOnly, aiExtracted }) => {
              const rawValue = getNestedValue(fields, key);
              const labelAi = !!(aiExtracted && !isEditing && !isValueMissing(rawValue));
              const isMissing = !isEditing && isValueMissing(rawValue);
              return (
                <div key={key} data-qd-missing={isMissing ? "true" : undefined} className="flex min-w-0 flex-col space-y-1">
                  <label className="flex flex-wrap items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
                    {label.includes('*') ? (
                      <>
                        {label.replace(' *', '')}
                        <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>
                      </>
                    ) : (
                      label
                    )}
                    {labelAi ? wrapWithAiTooltip(<AiBadge />, true) : null}
                  </label>
                  {isEditing && !readOnly ? (
                    <input
                      type={type}
                      value={rawValue}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      className="w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 outline-none transition-colors focus:border-fk-accent focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                    />
                  ) : (
                    wrapWithAiTooltip(
                      <div
                        className={`min-h-[34px] rounded-lg border px-2.5 py-1.5 text-[13px] font-medium transition-colors ${
                          isMissing
                            ? "border-dashed border-slate-200 bg-slate-50/50 text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.3)]"
                            : "border-transparent bg-slate-50 text-slate-700 dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-200"
                        }`}
                      >
                        {renderValueOrMissing(rawValue, "min-w-0 break-words", "badge")}
                      </div>,
                      labelAi
                    )
                  )}
                </div>
              );
            })}
          </div>

          {fields.packages?.length > 0 && (
            <div>
              <button
                onClick={() => setIsPackagesOpen(!isPackagesOpen)}
                className="group flex w-full items-center gap-2.5 rounded-xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/60 px-3 py-2 transition-all hover:border-slate-300 hover:shadow-sm dark:border-[oklch(0.3_0.02_264/0.6)] dark:from-slate-800/30 dark:to-slate-800/10 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
              >
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-fk-accent/10 text-fk-accent dark:bg-blue-500/15 dark:text-blue-300">
                  <Package className="h-3 w-3" />
                </span>
                <h3 className="flex-1 text-left text-[12.5px] font-semibold text-slate-900 dark:text-white">
                  Packages
                  <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300">
                    {fields.packages.length}
                  </span>
                </h3>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 dark:text-slate-500 ${isPackagesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isPackagesOpen && (
                <div className="mt-2 max-h-[min(70dvh,40rem)] space-y-2 overflow-y-auto overscroll-y-contain pr-1 pb-1 animate-in slide-in-from-top-2 duration-200">
                  {fields.packages.map((pkg, idx) => (
                    <div
                      key={idx}
                      className="space-y-2.5 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/40 p-3 dark:border-[oklch(0.3_0.02_264/0.6)] dark:from-slate-800/30 dark:to-slate-800/10"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-fk-accent/10 text-[10px] font-bold text-fk-accent dark:bg-blue-500/15 dark:text-blue-300">
                            {idx + 1}
                          </span>
                          <span className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-slate-600 dark:text-slate-300">
                            Package {idx + 1}
                          </span>
                        </div>
                        {isEditing && idx !== 0 && (
                          <button
                            onClick={() =>
                              setFields(prev => ({
                                ...prev,
                                packages: prev.packages.filter((_, index) => idx !== index),
                              }))
                            }
                            className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                            title="Remove package"
                          >
                            <AlertCircle size={12} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-2">
                        {packageFieldDefinitions.map(({ key, label, type, non_editable, aiExtracted }) => {
                          const rawValue = pkg[key];
                          const labelAi = !!(aiExtracted && !isEditing && !isValueMissing(rawValue));
                          const isMissing = !isEditing && isValueMissing(rawValue);
                          return (
                            <div key={key} data-qd-missing={isMissing ? "true" : undefined} className="min-w-0 space-y-1">
                              <label className="flex flex-wrap items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
                                {label.includes('*') ? (
                                  <>
                                    {label.replace(' *', '')}
                                    <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>
                                  </>
                                ) : (
                                  label
                                )}
                                {labelAi ? wrapWithAiTooltip(<AiBadge />, true) : null}
                              </label>
                              {isEditing && !non_editable ? (
                                <input
                                  type={type}
                                  checked={type === 'checkbox' ? !!pkg[key] : undefined}
                                  value={type !== 'checkbox' ? (pkg[key] || '') : undefined}
                                  onChange={(e) => handlePackageChange(
                                    idx,
                                    key,
                                    type === 'checkbox' ? e.target.checked : e.target.value,
                                    type
                                  )}
                                  className="w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-900 outline-none transition-colors focus:border-fk-accent focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                                />
                              ) : (
                                wrapWithAiTooltip(
                                  <div
                                    className={`min-h-[32px] rounded-lg border px-2.5 py-1.5 text-[13px] font-medium transition-colors ${
                                      isMissing
                                        ? "border-dashed border-slate-200 bg-slate-50/50 text-slate-500 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.3)]"
                                        : "border-transparent bg-slate-50 text-slate-700 dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-200"
                                    }`}
                                  >
                                    {renderValueOrMissing(rawValue, "min-w-0 break-words", "badge")}
                                  </div>,
                                  labelAi
                                )
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div
            className={`grid gap-2 ${isEditing ? "grid-cols-1" : hideRequestMissingActions ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}
          >
            {isEditing ? (
              <button
                disabled={!isPackagesOpen}
                onClick={() =>
                  setFields(prev => ({
                    ...prev,
                    packages: [
                      ...prev.packages,
                      {
                        description: '',
                        length: '',
                        width: '',
                        height: '',
                        weight: '',
                        quantity: 1,
                      },
                    ],
                  }))
                }
                className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-[12px] font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Package size={13} />
                Add Package
              </button>
            ) : (
              <>
                {!hideRequestMissingActions && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-200/80 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-600 transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/15"
                    disabled={
                      fields.isMinContainerDetailFulfilled &&
                      fields.packages.every(p => p.isMinPackageDetailFulfilled)
                    }
                    onClick={() => generateMailForMissingFields("customer")}
                  >
                    <AlertCircle size={13} />
                    Request Missing
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* Hoisted out of the ring's render so the subtitle can state the same counts
     in words. One derivation, two readings — a ring that says 12 while the line
     under it says something else is the kind of drift this file has form for. */
  const totalFields = countFillableShipmentFields(fields);
  const filledFields = totalFields - countMissingShipmentFields(fields).total;
  const fieldsDone = totalFields > 0 && filledFields === totalFields;
  const RING_R = 15;
  const RING_CIRC = 2 * Math.PI * RING_R;
  const ringOffset = RING_CIRC * (1 - (totalFields ? filledFields / totalFields : 0));

  return (
    <div
      className="qw-card mb-4"
    >
      <button
        onClick={() => setIsContainerOpen(!isContainerOpen)}
        className="flex items-center gap-3 w-full p-5 rounded-xl transition-colors bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
      >
        {/* Completeness ring: captured / total fields the operator can fill —
            same rule as every "N missing" badge.

            The fraction used to live INSIDE the ring, and could not: the hole is
            26px across and "12/16" at 11px mono is about 33px wide, so any
            two-digit-over-two-digit reading collided with its own stroke. Only
            the captured count sits in there now — one or two characters, with
            room around them — and the total moved to the line below, which was
            spending itself restating the heading. */}
        <div className="relative h-11 w-11 shrink-0" aria-hidden>
          <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="22" cy="22" r={RING_R} fill="none" stroke="var(--line)" strokeWidth="4" />
            <circle cx="22" cy="22" r={RING_R} fill="none" stroke={fieldsDone ? "var(--emerald)" : "var(--blue)"} strokeWidth="4" strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={ringOffset} style={{ transition: "stroke-dashoffset .7s cubic-bezier(.3,.8,.3,1)" }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <span className="qw-mono text-[13px] font-bold leading-none tabular-nums text-slate-700 dark:text-slate-200">
              {filledFields}
            </span>
          </div>
        </div>
        <div className="flex-1 text-left">
          {/* "Container" reads as sea freight to an air-freight operator; the
              persisted key stays containerDetails, only the label moves. */}
          <h2 className="text-[14px] font-semibold text-slate-900 dark:text-white">
            Shipment details
          </h2>
          {/* Was "Shipment information and package details" — a restatement of
              the heading directly above it. It carries the count now, which is
              the thing the ring can only approximate, and it reads to a screen
              reader where the ring is aria-hidden. */}
          <p className="text-[12px] text-slate-500 dark:text-slate-400">
            {totalFields === 0
              ? "No fields to capture yet"
              : fieldsDone
                ? `All ${totalFields} fields captured`
                : `${filledFields} of ${totalFields} fields captured`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            onClick={(e) => {
              e.stopPropagation();
              void toggleEditing();
            }}
            className={`flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer ${
              isEditing
                ? 'bg-fk-accent text-white hover:bg-blue-600'
                : 'bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]'
            }`}
          >
            <FontAwesomeIcon icon={isEditing ? faSave : faPencil} className="mr-1.5 text-[10px]" />
            {isEditing ? 'Save' : 'Edit'}
          </span>
          <FontAwesomeIcon
            icon={faChevronDownIcon}
            className={`text-[12px] text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isContainerOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isContainerOpen && (
        <div className="p-5 animate-in slide-in-from-top-2 duration-200">
          <div className="relative grid grid-cols-1 gap-3.5">
            {/* Magnifier sweep (design motion): while parsing/re-parsing, a
                violet lens glides down the fields "reading" them. */}
            {(parsing || sweeping) && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 z-[2]"
                style={{ height: "64px", background: "linear-gradient(180deg, transparent, var(--violet-soft, rgba(124,58,237,0.12)), transparent)", animation: "fkqw-magnify 2.4s ease-in-out infinite" }}
              >
                <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", width: "30px", height: "30px", borderRadius: "50%", border: "2px solid var(--violet, #7c3aed)", boxShadow: "0 0 0 4px var(--violet-soft, rgba(124,58,237,0.12))" }}>
                  <div style={{ position: "absolute", right: "-6px", bottom: "-6px", width: "11px", height: "3px", background: "var(--violet, #7c3aed)", borderRadius: "2px", transform: "rotate(45deg)" }} />
                </div>
              </div>
            )}
            {containerFieldDefinitions.map(({ key, label, type, readOnly, aiExtracted }) => {
              const rawValue = getNestedValue(fields, key);
              const filled = !isValueMissing(rawValue);
              const state = getFieldState(aiExtracted, filled);
              return (
                <div key={key} data-qd-missing={!isEditing && !filled ? "true" : undefined} className="flex min-w-0 flex-col space-y-1.5">
                  <div className="flex items-center gap-2">
                    <label className="flex flex-wrap items-center gap-1.5 text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
                      {label.includes('*') ? (
                        <>
                          {label.replace(' *', '')}
                          <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>
                        </>
                      ) : (
                        label
                      )}
                    </label>
                    <span className="flex-1" />
                    {!isEditing && <FieldStateChip state={state} />}
                  </div>
                  {isEditing && !readOnly ? (
                    <input
                      type={type}
                      value={rawValue}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 transition-colors focus:border-fk-accent focus:outline-none focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                    />
                  ) : filled ? (
                    <div className="relative min-h-[46px] rounded-xl border border-slate-200/80 bg-white/85 px-3.5 py-2.5 pr-11 text-[14px] font-semibold text-slate-800 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100">
                      <span className="block min-w-0 break-words">{resolveDisplayValue(rawValue)}</span>
                      <span
                        aria-hidden
                        className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full"
                        style={{ background: "var(--emerald-soft, rgba(5,150,105,0.1))", color: "var(--emerald, #059669)" }}
                      >
                        <Check size={13} strokeWidth={2.5} />
                      </span>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] font-medium italic"
                      style={{ color: state.color, borderColor: "var(--card-border, #e2e8f0)", background: "var(--card-2, rgba(255,255,255,0.55))" }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: state.color, flex: "none" }} aria-hidden />
                      {state.placeholder}
                    </div>
                  )}
                  {/* Chargeable-weight balance beam (design motion): actual vs
                      volumetric, tilting — now with the calculated weights on
                      each pan so the comparison reads complete. */}
                  {key === "chargeable_volume_weight" && !isEditing && (
                    <div aria-hidden className="mt-1 flex items-end justify-center gap-3 pt-2">
                      <div className="text-center" style={{ animation: "fkqw-seesaw-l 3.6s ease-in-out infinite" }}>
                        <div className="qw-mono text-[10.5px] font-semibold text-slate-700 dark:text-slate-200">
                          {isValueMissing(getNestedValue(fields, "net_weight")) ? "—" : getNestedValue(fields, "net_weight")}
                          <span className="ml-0.5 text-[8px] font-normal text-slate-400">kg</span>
                        </div>
                        <div className="qw-mono mt-0.5 text-[9px] text-slate-400 dark:text-slate-500">actual</div>
                      </div>
                      <div style={{ position: "relative", width: "58px", height: "30px" }}>
                        <div style={{ position: "absolute", left: "50%", bottom: 0, transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderBottom: "8px solid var(--ink-3)", opacity: 0.5 }} />
                        <div style={{ position: "absolute", left: "50%", bottom: "8px", width: "2px", height: "9px", background: "var(--ink-3)", transform: "translateX(-50%)" }} />
                        <div style={{ position: "absolute", left: "50%", bottom: "17px", width: "46px", height: "2px", background: "linear-gradient(90deg, var(--blue), var(--violet))", borderRadius: "2px", transform: "translateX(-50%)", transformOrigin: "center", animation: "fkqw-tilt 3.6s ease-in-out infinite" }}>
                          <div style={{ position: "absolute", left: "50%", top: "-3px", width: "6px", height: "6px", borderRadius: "50%", background: "var(--card-solid)", border: "2px solid var(--blue)", transform: "translateX(-50%)" }} />
                        </div>
                      </div>
                      <div className="text-center" style={{ animation: "fkqw-seesaw-r 3.6s ease-in-out infinite" }}>
                        <div className="qw-mono text-[10.5px] font-semibold text-slate-700 dark:text-slate-200">
                          {isValueMissing(getNestedValue(fields, "net_volume_weight")) ? "—" : getNestedValue(fields, "net_volume_weight")}
                          <span className="ml-0.5 text-[8px] font-normal text-slate-400">kg</span>
                        </div>
                        <div className="qw-mono mt-0.5 text-[9px] text-slate-400 dark:text-slate-500">volumetric</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {fields.packages?.length > 0 && (
            <div className="mt-6">
              <button
                onClick={() => setIsPackagesOpen(!isPackagesOpen)}
                className="flex items-center gap-3 w-full p-3 bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] hover:bg-slate-100 dark:hover:bg-[oklch(0.26_0.028_264/0.5)] rounded-xl border border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)] transition-colors"
              >
                <PackagesStackIcon />
                <h3 className="text-[13px] font-semibold text-slate-900 dark:text-white flex-1 text-left">
                  Packages ({fields.packages.length})
                </h3>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isPackagesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isPackagesOpen && (
                <div className="mt-3 max-h-[min(70dvh,40rem)] space-y-3 overflow-y-auto overscroll-y-contain pr-1 pb-2 animate-in slide-in-from-top-2 duration-200">
                  {fields.packages.map((pkg, idx) => (
                    <div key={idx} className="p-4 border border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)] rounded-xl bg-slate-50/50 dark:bg-[oklch(0.26_0.028_264/0.3)] space-y-3">
                      <div className='flex justify-between items-center'>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-fk-accent/10 dark:bg-blue-500/15 rounded-full flex items-center justify-center">
                            <span className="text-[11px] font-semibold text-fk-accent dark:text-blue-400">{idx + 1}</span>
                          </div>
                          <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">Package {idx + 1}</span>
                        </div>
                        {isEditing && idx !== 0 && (
                          <button
                            onClick={() => setFields(prev => ({
                              ...prev,
                              packages: prev.packages.filter((_, index) => idx !== index),
                            }))}
                            className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full transition-colors"
                            title="Remove package"
                          >
                            <AlertCircle size={14} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-3.5">
                        {packageFieldDefinitions.map(({ key, label, type, non_editable, aiExtracted }) => {
                          const rawValue = pkg[key];
                          const filled = type === 'checkbox' ? true : !isValueMissing(rawValue);
                          const state = getFieldState(aiExtracted, filled);
                          return (
                            <div key={key} data-qd-missing={!isEditing && !filled ? "true" : undefined} className="min-w-0 space-y-1.5">
                              <div className="flex items-center gap-2">
                                <label className="flex flex-wrap items-center gap-1.5 text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
                                  {label.includes('*') ? (
                                    <>
                                      {label.replace(' *', '')}
                                      <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>
                                    </>
                                  ) : (
                                    label
                                  )}
                                </label>
                                <span className="flex-1" />
                                {!isEditing && <FieldStateChip state={state} />}
                              </div>
                              {isEditing && !non_editable ? (
                                <input
                                  type={type}
                                  checked={type === 'checkbox' ? !!pkg[key] : undefined}
                                  value={type !== 'checkbox' ? (pkg[key] || '') : undefined}
                                  onChange={(e) => handlePackageChange(
                                    idx,
                                    key,
                                    type === 'checkbox' ? e.target.checked : e.target.value,
                                    type
                                  )}
                                  className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 transition-colors focus:border-fk-accent focus:outline-none focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                                />
                              ) : filled ? (
                                <div className="relative min-h-[46px] rounded-xl border border-slate-200/80 bg-white/85 px-3.5 py-2.5 pr-11 text-[14px] font-semibold text-slate-800 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100">
                                  <span className="block min-w-0 break-words">{type === 'checkbox' ? (rawValue ? 'Yes' : 'No') : resolveDisplayValue(rawValue)}</span>
                                  <span aria-hidden className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full" style={{ background: "var(--emerald-soft, rgba(5,150,105,0.1))", color: "var(--emerald, #059669)" }}>
                                    <Check size={13} strokeWidth={2.5} />
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] font-medium italic" style={{ color: state.color, borderColor: "var(--card-border, #e2e8f0)", background: "var(--card-2, rgba(255,255,255,0.55))" }}>
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: state.color, flex: "none" }} aria-hidden />
                                  {state.placeholder}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            {isEditing && (
              <button
                disabled={!isPackagesOpen}
                onClick={() =>
                  setFields(prev => ({
                    ...prev,
                    packages: [
                      ...prev.packages,
                      {
                        description: '',
                        length: '',
                        width: '',
                        height: '',
                        weight: '',
                        quantity: 1,
                      },
                    ],
                  }))
                }
                className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Package size={13} />
                Add Package
              </button>
            )}

          </div>

          {/* Footer CTA (design "Redesign 2"): full-width amber "Request N from
              customer" — the count is what the email will actually ask for. */}
          {!isEditing && !hideRequestMissingActions && (() => {
            const askCount = countMissingShipmentFields(fields).total;
            const nothingToAsk = askCount === 0;
            return (
              <div className="mt-4 border-t border-slate-100 pt-3.5 dark:border-[oklch(0.3_0.02_264/0.6)]">
                <button
                  type="button"
                  disabled={nothingToAsk}
                  onClick={() => { playSweep(); generateMailForMissingFields("customer"); }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl px-3 py-3 text-[13.5px] font-bold text-white transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  style={{
                    background: nothingToAsk
                      ? "var(--ink-3, #94a3b8)"
                      : "linear-gradient(120deg, var(--amber, #f59e0b), #f59e0b)",
                    boxShadow: nothingToAsk
                      ? "none"
                      : "0 10px 22px -10px var(--amber, #f59e0b)",
                  }}
                >
                  <Mail size={16} strokeWidth={2.5} aria-hidden />
                  {nothingToAsk
                    ? "Nothing to request"
                    : `Request ${askCount} from customer`}
                </button>
              </div>
            );
          })()}

          {/* <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
            {isEditing ? (
              <button
                onClick={() => setFields(prev => ({
                  ...prev,
                  packages: [
                    ...prev.packages,
                    {
                      description: '',
                      length: '',
                      width: '',
                      height: '',
                      weight: '',
                      quantity: 1,
                    },
                  ],
                }))}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200"
              >
                <Package size={16} />
                Add New Package
              </button>
            ) : (
              <>
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  disabled={fields.isMinContainerDetailFulfilled && fields.packages.every(p => p.isMinPackageDetailFulfilled)}
                  onClick={() => generateMailForMissingFields("customer")}
                >
                  <AlertCircle size={16} />
                  Request Missing Fields
                </button>
                <button
                  onClick={updateContainerEntry}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  disabled={updateContainerButtonDisabled}
                >
                  <CheckCircle size={16} />
                  Update Container
                </button>
              </>
            )}
            <div className="flex justify-end mb-4 sticky top-0 z-10 bg-white/90 dark:bg-[oklch(0.225_0.026_264)]/90 backdrop-blur border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] rounded-t-xl px-2 py-2">
              {isEditing ? (
                <button
                  onClick={toggleEditing}
                  className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
                >
                  <Save size={14} className="mr-1.5" /> Save Changes
                </button>
              ) : (
                <button
                  onClick={toggleEditing}
                  className="flex items-center px-3 py-1.5 bg-gray-100 dark:bg-[oklch(0.3_0.024_264)] hover:bg-gray-200 dark:hover:bg-[oklch(0.38_0.022_264)] text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-200 font-medium text-sm"
                >
                  <Pencil size={14} className="mr-1.5" /> Edit Details
                </button>
              )}
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
}

function CustomerDetails({
  isEditing,
  toggleEditing,
  customer,
  setCustomer,
  updateCustomerEntry,
  updateCustomerButtonDisabled,
  openTrigger,
  compactMode = false
}) {
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  useEffect(() => {
    setIsCustomerOpen(true);
  }, [openTrigger]);

  return (
    <div
      className="rounded-xl mt-5 mb-4 bg-white/72 border border-slate-200/80 shadow-card dark:bg-[oklch(0.225_0.026_264/0.94)] dark:border-[oklch(0.3_0.02_264/0.6)]"
    >
      <button
        onClick={() => setIsCustomerOpen(!isCustomerOpen)}
        className="flex items-center gap-3 w-full p-5 rounded-t-xl transition-colors bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
      >
        <FontAwesomeIcon icon={faUserIcon} className="text-lg text-fk-accent" />
        <div className="flex-1 text-left">
          <h2 className="text-[14px] font-semibold text-slate-900 dark:text-white">
            Customer Information
          </h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">
            Contact details
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleEditing();
            }}
            className={`flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer ${
              isEditing
                ? 'bg-fk-accent text-white hover:bg-blue-600'
                : 'bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]'
            }`}
          >
            <FontAwesomeIcon icon={isEditing ? faSave : faPencil} className="mr-1.5 text-[10px]" />
            {isEditing ? 'Save' : 'Edit'}
          </span>
          <FontAwesomeIcon
            icon={faChevronDownIcon}
            className={`text-[12px] text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isCustomerOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isCustomerOpen && (
        <div className="p-5">
          <div className="grid grid-cols-1 gap-3.5">
            {customerFieldDefinitions.map(({ key, label, type, readOnly, aiExtracted }) => {
              const rawValue = customer[key];
              const filled = !isValueMissing(rawValue);
              const state = getFieldState(aiExtracted, filled);
              return (
                <div key={key} data-qd-missing={!isEditing && !filled ? "true" : undefined} className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <label className="flex flex-wrap items-center gap-1.5 text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
                      {label}
                    </label>
                    <span className="flex-1" />
                    {!isEditing && <FieldStateChip state={state} />}
                  </div>
                  {isEditing && !readOnly ? (
                    <input
                      type={type}
                      value={customer[key] || ''}
                      onChange={(e) => setCustomer(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 transition-colors focus:border-fk-accent focus:outline-none focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                    />
                  ) : filled ? (
                    <div className="relative min-h-[46px] rounded-xl border border-slate-200/80 bg-white/85 px-3.5 py-2.5 pr-11 text-[14px] font-semibold text-slate-800 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100">
                      <span className="block min-w-0 break-words">{resolveDisplayValue(rawValue)}</span>
                      <span aria-hidden className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full" style={{ background: "var(--emerald-soft, rgba(5,150,105,0.1))", color: "var(--emerald, #059669)" }}>
                        <Check size={13} strokeWidth={2.5} />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] font-medium italic" style={{ color: state.color, borderColor: "var(--card-border, #e2e8f0)", background: "var(--card-2, rgba(255,255,255,0.55))" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: state.color, flex: "none" }} aria-hidden />
                      {state.placeholder}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!isEditing && (
            <div className="mt-5 flex justify-end">
              <button
                onClick={updateCustomerEntry}
                disabled={updateCustomerButtonDisabled}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                  updateCustomerButtonDisabled
                    ? 'bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-400 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-fk-accent text-white hover:bg-blue-600'
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircleIcon} className="text-[11px]" />
                Update Customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShipperDetails({
  isEditing,
  toggleEditing,
  shipper,
  handleShipperChange,
  updateShipperEntry,
  initialShipper,
  updateShipperButtonDisabled,
  generateMailForMissingFields,
  openTrigger,
  hideRequestMissingActions = false,
}) {
  const [isShipperOpen, setIsShipperOpen] = useState(false);
  useEffect(() => {
    setIsShipperOpen(true);
  }, [openTrigger]);
  const hasShipperEmail = !!initialShipper.email;

  return (
    <div className="bg-white/72 dark:bg-[oklch(0.225_0.026_264/0.94)] rounded-xl shadow-card border border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)] mt-5">
      <button
        onClick={() => setIsShipperOpen(!isShipperOpen)}
        className="flex items-center gap-3 w-full p-5 rounded-t-xl transition-colors bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
      >
        <Truck className="h-5 w-5 text-fk-accent" />
        <div className="flex-1 text-left">
          <h2 className="text-[14px] font-semibold text-slate-900 dark:text-white">Shipper Information</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Shipper contact and collection details</p>
        </div>
        <div className="flex items-center gap-2">
          {hasShipperEmail && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing();
              }}
              className={`flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer ${
                isEditing
                  ? 'bg-fk-accent text-white hover:bg-blue-600'
                  : 'bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]'
              }`}
            >
              <FontAwesomeIcon icon={isEditing ? faSave : faPencil} className="mr-1.5 text-[10px]" />
              {isEditing ? 'Save' : 'Edit'}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isShipperOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isShipperOpen && (
        <div className="p-5 animate-in slide-in-from-top-2 duration-200">

          {hasShipperEmail ? (
            <>
              <div className="grid grid-cols-1 gap-3.5">
                {shipperFileDefinitions.map(({ key, label, type }) => {
                  const rawValue = shipper[key];
                  const filled = !isValueMissing(rawValue);
                  const state = getFieldState(false, filled);
                  return (
                    <div key={key} data-qd-missing={!isEditing && !filled ? "true" : undefined} className="min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <label className="flex flex-wrap items-center gap-1.5 text-[12.5px] font-bold text-slate-800 dark:text-slate-100">{label}</label>
                        <span className="flex-1" />
                        {!isEditing && <FieldStateChip state={state} />}
                      </div>
                      {isEditing ? (
                        <input
                          type={type}
                          checked={typeof shipper[key] === 'boolean' ? shipper[key] : undefined}
                          value={typeof shipper[key] !== 'boolean' ? shipper[key] || '' : undefined}
                          onChange={(e) => handleShipperChange(
                            key,
                            typeof shipper[key] === 'boolean' ? e.target.checked : e.target.value
                          )}
                          className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 transition-colors focus:border-fk-accent focus:outline-none focus:ring-2 focus:ring-fk-accent/10 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200"
                          disabled={key === "email"}
                        />
                      ) : filled ? (
                        <div className="relative min-h-[46px] rounded-xl border border-slate-200/80 bg-white/85 px-3.5 py-2.5 pr-11 text-[14px] font-semibold text-slate-800 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-100">
                          <span className="block min-w-0 break-words">{resolveDisplayValue(rawValue)}</span>
                          <span aria-hidden className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full" style={{ background: "var(--emerald-soft, rgba(5,150,105,0.1))", color: "var(--emerald, #059669)" }}>
                            <Check size={13} strokeWidth={2.5} />
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] font-medium italic" style={{ color: state.color, borderColor: "var(--card-border, #e2e8f0)", background: "var(--card-2, rgba(255,255,255,0.55))" }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: state.color, flex: "none" }} aria-hidden />
                          {state.placeholder}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {!isEditing && (
                <div className="mt-5 flex flex-wrap gap-2 justify-end">
                  {!hideRequestMissingActions && (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-lg text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                      disabled={shipperFileDefinitions.every(val => shipper[val.key] !== null && shipper[val.key] !== undefined && shipper[val.key] !== '')}
                      onClick={() => generateMailForMissingFields('shipper', 'data_request')}
                    >
                      <AlertCircle size={13} />
                      Request Missing
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={updateShipperEntry}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-fk-accent hover:bg-blue-600 text-white rounded-lg text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                    disabled={updateShipperButtonDisabled}
                  >
                    <CheckCircle size={13} />
                    Update Shipper
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className='mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg'>
                <div className="flex flex-col items-center gap-2 text-amber-700 dark:text-amber-400 text-[13px]">
                  <AnimatedLoader variant="warn" size={72} label="No shipper information" />
                  <span className="font-medium">No Shipper Information Available</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Shipper Email Address</label>
                  <input
                    type='email'
                    value={shipper.email || ''}
                    onChange={(e) => handleShipperChange("email", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-[13px] border border-slate-200 dark:border-[oklch(0.33_0.022_264)] bg-white dark:bg-[oklch(0.26_0.028_264)] text-slate-900 dark:text-slate-200 focus:border-fk-accent focus:ring-2 focus:ring-fk-accent/10 focus:outline-none transition-colors"
                    placeholder="Enter shipper email address"
                  />
                </div>
                {!shipper.email ? (
                  hideRequestMissingActions ? null : (
                  <button
                    type="button"
                    onClick={() => generateMailForMissingFields("customer", "shipper_details_request")}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-lg text-[12px] font-medium transition"
                  >
                    <AlertCircle size={13} />
                    Request Shipper Email from Customer
                  </button>
                  )
                ) : (
                  <button
                    onClick={updateShipperEntry}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-fk-accent hover:bg-blue-600 text-white rounded-lg text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                    disabled={updateShipperButtonDisabled}
                  >
                    <CheckCircle size={13} />
                    Save Shipper Email
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FinalAgentDetails({ finalAgent, type = "agent", openTrigger, setRefetchTrigger }) {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const loggedInUser = useSelector((state) => state.reducer.userLoggedIn);

  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const detail = type === "agent" ? finalAgent?.agentDetail : finalAgent?.airlineDetail;
  // `type` doubles as a logic key ("agent") and as on-screen prose. The party is
  // called a Transporter everywhere else in the app, so only the prose changes.
  const typeLabel = type === "agent" ? TRANSPORTER.toLowerCase() : type;

  const [editValues, setEditValues] = useState({
    phone_number: detail?.phone_number || "",
    contact_person: detail?.contact_person || "",
    address: detail?.address || "",
  });

  useEffect(() => {
    setIsAgentOpen(true);
  }, [openTrigger]);

  const handleCancel = () => {
    setEditValues({
      phone_number: detail?.phone_number || "",
      contact_person: detail?.contact_person || "",
      address: detail?.address || "",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      if (type === "agent") {
        await editUsers({
          token: loginToken,
          id: detail._id,
          role: loggedInUser?.role,
          data: { role: "agent", ...editValues },
        });
      } else {
        await updateThisAirline({
          token: loginToken,
          airlineId: detail._id,
          role: loggedInUser?.role,
          data: editValues,
        });
      }
      showSwal("success", `${type === "agent" ? TRANSPORTER : "Airline"} details updated!`, 200);
      setIsEditing(false);
      setRefetchTrigger?.((prev) => prev + 1);
    } catch (err) {
      console.error("Error updating details:", err);
      showSwal("error", "Failed to update details");
    }
  };

  const editableFields = [
    { key: "phone_number", label: "Phone" },
    { key: "contact_person", label: "Contact Person" },
    { key: "address", label: "Address" },
  ];

  const headerButtons = detail ? (
    <div className="flex items-center gap-2">
      {isEditing ? (
        <>
          <span
            onClick={(e) => { e.stopPropagation(); handleCancel(); }}
            className="flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]"
          >
            Cancel
          </span>
          <span
            onClick={(e) => { e.stopPropagation(); handleSave(); }}
            className="flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer bg-fk-accent text-white hover:bg-blue-600"
          >
            <FontAwesomeIcon icon={faSave} className="mr-1.5 text-[10px]" />
            Save
          </span>
        </>
      ) : (
        <span
          onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
          className="flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors cursor-pointer bg-slate-100 dark:bg-[oklch(0.26_0.028_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[oklch(0.3_0.024_264)]"
        >
          <FontAwesomeIcon icon={faPencil} className="mr-1.5 text-[10px]" />
          Edit
        </span>
      )}
    </div>
  ) : null;

  if (!finalAgent?.agentDetail && !finalAgent?.airlineDetail) return (
    <div className="bg-white/72 dark:bg-[oklch(0.225_0.026_264/0.94)] rounded-xl shadow-card border border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)] mt-5">
      <button
        onClick={() => setIsAgentOpen(!isAgentOpen)}
        className="flex items-center gap-3 w-full p-5 rounded-t-xl transition-colors bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
      >
        {type === "agent" ? <Truck className="h-5 w-5 text-fk-accent" /> : <Plane className="h-5 w-5 text-fk-accent" />}
        <div className="flex-1 text-left">
          <h2 className="text-[14px] font-semibold text-slate-900 dark:text-white capitalize">Final {typeLabel} Information</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Selected {typeLabel} details and rates</p>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isAgentOpen ? 'rotate-180' : ''}`} />
      </button>

      {isAgentOpen && (
        <div className="p-5">
          <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
            <div className="flex flex-col items-center gap-2 text-red-600 dark:text-red-400 text-[13px]">
              <AnimatedLoader variant="stamp" size={120} label="Awaiting confirmation" />
              <span className="font-medium capitalize">Final {type} not confirmed yet</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const readOnlyFields = [
    ["Name", detail?.name],
    ["Email", detail?.email_address],
    ["Final Rate", finalAgent.finalRate],
    ["Status", finalAgent.status],
  ];

  return (
    <div className="bg-white/72 dark:bg-[oklch(0.225_0.026_264/0.94)] rounded-xl shadow-card border border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)] mt-5">
      <button
        onClick={() => setIsAgentOpen(!isAgentOpen)}
        className="flex items-center gap-3 w-full p-5 rounded-t-xl transition-colors bg-slate-50/80 dark:bg-[oklch(0.26_0.028_264/0.3)] border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.5)]"
      >
        {type === "agent" ? <Truck className="h-5 w-5 text-fk-accent" /> : <Plane className="h-5 w-5 text-fk-accent" />}
        <div className="flex-1 text-left">
          <h2 className="text-[14px] font-semibold text-slate-900 dark:text-white capitalize">Final {typeLabel} Information</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Selected {typeLabel} details and rates</p>
        </div>
        {headerButtons}
        <ChevronDown className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isAgentOpen ? 'rotate-180' : ''}`} />
      </button>

      {isAgentOpen && (
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {readOnlyFields.map(([label, value]) => (
              <div key={label} className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</label>
                <div className="min-h-[38px] rounded-lg bg-slate-50 px-3 py-2 text-[13px] font-medium text-slate-700 dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-300">
                  {renderValueOrMissing(value, "min-w-0 break-words")}
                </div>
              </div>
            ))}
            {editableFields.map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editValues[key]}
                    onChange={(e) => setEditValues((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-[13px] focus:outline-none bg-white dark:bg-[oklch(0.26_0.028_264)] border border-slate-200 dark:border-[oklch(0.33_0.022_264)] text-slate-900 dark:text-slate-200 focus:border-fk-accent focus:ring-2 focus:ring-fk-accent/10 transition-colors"
                  />
                ) : (
                  <div className="min-h-[38px] rounded-lg bg-slate-50 px-3 py-2 text-[13px] font-medium text-slate-700 dark:bg-[oklch(0.26_0.028_264/0.5)] dark:text-slate-300">
                    {renderValueOrMissing(editValues[key], "min-w-0 break-words")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function QuoteDetails({
  quote = {},
  triggerPanel,
  C_EMAIL,
  setS_EMAIL,
  defaultOpen,
  setRefetchTrigger,
  currentStage,
  compactMode = false,
  hideAssistant = false,
  hideRequestMissingActions = false,
  iconRailOnly = false,
  onToggleInspectorRail,
  onExpandInspectorFromTab,
  containerInspectorEpoch = 0,
  onReparse,
  reparseMode = "rerun",
  reparseInFlight = false,
}) {
  const loggedInUser = useSelector((state) => state?.reducer?.userLoggedIn);
  const initialContainer = quote?.containerDetails || {};
  const [initialShipper, setInitialShipper] = useState(quote?.shipperDetails || {});
  const [initialCustomer, setInitialCustomer] = useState(quote?.customerDetails || { name: '', email: '', phone: '', address: '' });
  const finalAgent = quote?.my_saved_agents?.filter(a => a._id == quote?.finalizedQuoteOption?.savedAgentId)[0];
  const finalAirline = quote?.my_saved_airlines?.filter(a => a._id == quote?.finalizedQuoteOption?.savedAirlineId)[0];

  const [isContainerEditing, setIsContainerEditing] = useState(false);
  const [isCustomerEditing, setIsCustomerEditing] = useState(false);
  const [isShipperEditing, setIsShipperEditing] = useState(false);
  const [shipper, setShipper] = useState(initialShipper);
  const [customer, setCustomer] = useState(initialCustomer);
  const [updateContainerButtonDisabled, setUpdateContainerButtonDisabled] = useState(true);
  const [updateShipperButtonDisabled, setUpdateShipperButtonDisabled] = useState(true);
  const [updateCustomerButtonDisabled, setUpdateCustomerButtonDisabled] = useState(true);

  const [hasUpdatedCustomerEmail, setHasUpdatedCustomerEmail] = useState(false);

  const calculateVolumeWeight = (pkg) => {
    const l = parseFloat(pkg.length) || 0;
    const w = parseFloat(pkg.width) || 0;
    const h = parseFloat(pkg.height) || 0;
    return (l * w * h) / 6000;
  };

  const recalculateWeights = (packages) => {
    let totalNetWeight = 0;
    let totalVolumeWeight = 0;
    let chargeable_volume_weight = 0

    const updated = packages.map(pkg => {
      const quantity = parseFloat(pkg.quantity) || 1;
      const actualWeight = parseFloat(pkg.weight) || 0;
      const volumeWeight = calculateVolumeWeight(pkg);
      // const chargeableWeight = Math.max(actualWeight, volumeWeight);

      totalNetWeight += actualWeight * quantity;
      totalVolumeWeight += volumeWeight * quantity;

      return {
        ...pkg,
        volume_weight: volumeWeight.toFixed(2),
        // chargeable_weight: chargeableWeight.toFixed(2),
      };
    });

    // let maxSumOfBoth = updated.reduce(((a, c) => ({ aw: a.aw + c.aw, vw: a.vw + c.vw })), { aw: 0, vw: 0 })
    chargeable_volume_weight = Math.max(totalVolumeWeight, totalNetWeight)

    return {
      packages: updated,
      net_weight: totalNetWeight.toFixed(2),
      net_volume_weight: totalVolumeWeight.toFixed(2),
      chargeable_volume_weight: chargeable_volume_weight.toFixed(2),
    };
  };

  const [fields, setFields] = useState(() => {
    const { packages, net_weight, net_volume_weight, chargeable_volume_weight } = recalculateWeights(initialContainer.packages || []);
    return {
      ...initialContainer,
      packages,
      net_weight,
      net_volume_weight,
      chargeable_volume_weight
    };
  });

  useEffect(() => {
    if (!containerInspectorEpoch) return;
    const base = quote?.containerDetails || {};
    const { packages, net_weight, net_volume_weight, chargeable_volume_weight } = recalculateWeights(
      base.packages || []
    );
    setFields({
      ...base,
      packages,
      net_weight,
      net_volume_weight,
      chargeable_volume_weight,
    });
    const cust = quote?.customerDetails || { name: "", email: "", phone: "", address: "" };
    const ship = quote?.shipperDetails || {};
    setCustomer(cust);
    setShipper(ship);
    setInitialCustomer(cust);
    setInitialShipper(ship);
  }, [containerInspectorEpoch]);

  useEffect(() => {
    setUpdateContainerButtonDisabled(deepEqualIgnoreFields(fields, initialContainer));
  }, [fields, initialContainer]);

  useEffect(() => {
    setUpdateShipperButtonDisabled(deepEqualIgnoreFields(shipper, initialShipper));
  }, [shipper, initialShipper]);

  useEffect(() => {
    setUpdateCustomerButtonDisabled(deepEqualIgnoreFields(customer, initialCustomer));
    if (!initialCustomer.email && !hasUpdatedCustomerEmail && C_EMAIL) {
      updateCustomerEntry("Updated Customer Email Successfully");
      setHasUpdatedCustomerEmail(true);
    }
  }, [customer, initialCustomer, hasUpdatedCustomerEmail, C_EMAIL]);

  const loginToken = useSelector((state) => state.reducer.userToken);

  const handleFieldChange = (key, value) => {
    setFields(prev => {
      const newFields = { ...prev };
      setNestedValue(newFields, key, value);
      return newFields;
    });
  };

  const handlePackageChange = (index, key, value, type) => {
    if (type === 'number') value = parseFloat(value) || 0;

    const updatedPackages = [...fields.packages];
    updatedPackages[index] = { ...updatedPackages[index], [key]: value };

    const { packages, net_weight, net_volume_weight, chargeable_volume_weight } = recalculateWeights(updatedPackages);

    setFields(prev => ({
      ...prev,
      packages,
      net_weight,
      net_volume_weight,
      chargeable_volume_weight
    }));
  };

  const handleShipperChange = (key, value) => {
    setShipper(prev => ({ ...prev, [key]: value }));
  };


  const updateContainerEntry = async () => {
    try {
      const payload = {
        ...fields,
        isMinContainerDetailFulfilled: undefined,
        packages: fields.packages.map(pkg => ({
          ...pkg,
          volume_weight: calculateVolumeWeight(pkg).toFixed(2),
          chargeable_weight: Math.max(parseFloat(pkg.weight) || 0, calculateVolumeWeight(pkg)).toFixed(2),
        })),
      };

      const response = await updateContainer({
        token: loginToken,
        containerId: initialContainer._id,
        role: loggedInUser?.role,
        payload,
      });
      setRefetchTrigger(prev => prev + 1);
      setFields(response.container);
      showSwal('success', 'Container updated successfully!', 200);
      return true;
    } catch (error) {
      console.error('Error updating container:', error);
      showSwal('error', 'Failed to update container');
      return false;
    }
  };

  const handleContainerEditToggle = async () => {
    if (isContainerEditing) {
      if (!updateContainerButtonDisabled) {
        const ok = await updateContainerEntry();
        if (!ok) return;
      }
      setIsContainerEditing(false);
    } else {
      setIsContainerEditing(true);
    }
  };

  const updateShipperEntry = async () => {
    try {
      const response = await updateShipperDetails({
        token: loginToken,
        shipperId: initialShipper._id,
        role: loggedInUser?.role,
        payload: {
          shipperDetails: {
            ...shipper,
            _id: undefined,
            updatedAt: undefined,
            createdAt: undefined
          }
        },
      });
      if (response?.shipper && initialShipper.email != response.shipper.email) {
        setS_EMAIL(response.shipper.email);
      }
      // Same guard as customer: never let a malformed response null the state.
      setInitialShipper(response?.shipper || shipper);
      setShipper(response?.shipper || shipper);
      showSwal('success', 'Shipper information updated!', 200);
    } catch (error) {
      console.error('Error updating shipper:', error);
      showSwal('error', 'Failed to update shipper');
    }
  };

  const updateCustomerEntry = async (message) => {
    try {
      const { customerDetails } = await updateCustomerDetails({
        token: loginToken,
        role: loggedInUser?.role,
        payload: { ...customer, quoteId: quote._id, email: C_EMAIL },
        role: loggedInUser?.role
      });
      // A malformed/failed response must NOT null the state — `customer` is
      // read unguarded in render (missing-count filters), so undefined here
      // white-screened the whole workspace ("reading 'name'").
      setInitialCustomer(customerDetails || customer);
      setCustomer(customerDetails || customer);
      showSwal('success', message ? message : 'Customer information updated!', 200);
    } catch (error) {
      console.error('Error updating customer:', error);
      showSwal('error', 'Failed to update customer');
    }
  };

  const generateMailForMissingFields = (to = "customer", task = 'data_request') => {
    const missingFields = (to === 'customer' ? containerFieldDefinitions : shipperFileDefinitions)
      .filter(field => !field.readOnly && !(to === "customer" ? fields : shipper)[field.key])
      .map(field => field.key);

    const missingPackageFields = task === 'data_request'
      ? fields.packages.flatMap((pkg, index) =>
        packageFieldDefinitions
          .filter(field => !field.non_editable && !pkg[field.key])
          .map(field => `package.${index}.${field.key}`)
      )
      : [];
    const combinedFields = task === 'data_request'
      ? [...missingFields, ...(to === 'customer' ? missingPackageFields : [])]
      : ["shipper.email"];

    triggerPanel(to, combinedFields, task);
  };

  const [activeDetailsTab, setActiveDetailsTab] = useState("container");

  // "N missing" pills are navigational: clicking a tab with missing fields
  // switches to it AND scrolls the first missing field into view with a brief
  // highlight ring; clicking the same tab again cycles to the next one.
  const jumpCycleRef = useRef({ tab: null, idx: 0 });
  const jumpToMissing = useCallback((tabKey) => {
    setActiveDetailsTab(tabKey);
    // Double rAF so the hidden→block panel swap has painted before we query.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const panel = document.querySelector(`[data-qd-panel="${tabKey}"]`);
        if (!panel) return;
        const targets = Array.from(
          panel.querySelectorAll('[data-qd-missing="true"]')
        ).filter((el) => el.offsetParent !== null); // visible only
        if (!targets.length) return;
        const cycle = jumpCycleRef.current;
        const idx = cycle.tab === tabKey ? cycle.idx % targets.length : 0;
        jumpCycleRef.current = { tab: tabKey, idx: idx + 1 };
        const el = targets[idx];
        const reduce =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
        el.classList.remove("fk-flash");
        void el.offsetWidth; // restart the animation if re-applied
        el.classList.add("fk-flash");
        setTimeout(() => el.classList.remove("fk-flash"), 1600);
      })
    );
  }, []);

  const tabIcons = {
    container: faBox,
    customer: faUserIcon,
    shipper: faTruckIcon,
    finalAgent: faCheckCircleIcon,
    finalAirline: faPlaneIcon
  };

  // Stage-aware: determine which tabs are relevant for the current stage
  const earlyStages = ["query_stage", "quote_stage"];
  const midStages = ["nomination_stage", "collection_stage", "airline_booking_stage", "documentation_stage"];
  const isTabAvailable = (tabKey) => {
    // console.log('tabKey:', tabKey, '\tcurrentStage:', currentStage, '\tmidStages.slice(0, 2):', midStages.slice(0, 2), '\n!earlyStages.includes(currentStage)', !earlyStages.includes(currentStage), '!midStages.slice(0, 2).includes(currentStage)', !midStages.slice(0, 2).includes(currentStage), '\nVAl:', !earlyStages.includes(currentStage) && !midStages.slice(0, 2).includes(currentStage), '\n\n')
    if (!currentStage) return true; // no stage info, show all
    if (tabKey === "container" || tabKey === "customer") return true; // always available
    if (tabKey === "shipper") return true; // available from nomination onward
    if (tabKey === "finalAgent" || tabKey === "finalAirline") {
      return !earlyStages.includes(currentStage) && midStages.slice(0, 2).includes(currentStage);
    }
    return true;
  };

  const containerMissingCount = countMissingShipmentFields(fields).total;

  const customerMissingCount = customerFieldDefinitions.filter(
    ({ key, readOnly }) => !readOnly && isValueMissing((customer || {})[key])
  ).length;

  const shipperMissingCount = shipperFileDefinitions.filter(
    ({ key }) => isValueMissing((shipper || {})[key])
  ).length;

  const tabItems = [
    {
      key: "container",
      label: "Shipment",
      // Lines AND pieces: Document Studio prints pieces on the AWB, so showing
      // only the line count made the two screens look like different shipments.
      description: describePackageTotals(fields.packages),
      status: containerMissingCount > 0 ? `${containerMissingCount} missing` : "Ready",
    },
    {
      key: "customer",
      label: "Customer",
      description: customer?.email || "Contact details to verify",
      status: customerMissingCount > 0 ? `${customerMissingCount} missing` : "Ready",
    },
    {
      key: "shipper",
      label: "Shipper",
      description: shipper.email || "Collection contact not connected",
      status: shipperMissingCount > 0 ? (shipper.email ? `${shipperMissingCount} missing` : "Not Connected Yet") : "Ready",
    },
    {
      key: "finalAgent",
      label: `Final ${TRANSPORTER}`,
      description: finalAgent?.agentDetail?.name || `No ${TRANSPORTER.toLowerCase()} nominated`,
      status: finalAgent?.agentDetail ? "Selected" : "Pending",
    },
    {
      key: "finalAirline",
      label: "Final Airline",
      description: finalAirline?.airlineDetail?.name || "No airline nominated",
      status: finalAirline?.airlineDetail ? "Selected" : "Pending",
    },
  ];

  const activeTabMeta = tabItems.find((t) => t.key === activeDetailsTab) || tabItems[0];

  const isFirstParse = reparseMode === "first";
  const reparseButton = typeof onReparse === "function" ? (
    <button
      type="button"
      onClick={onReparse}
      disabled={reparseInFlight}
      title={
        isFirstParse
          ? "Extract shipment fields from the original email with AI"
          : "Re-extract shipment fields from the email — your manual edits are preserved"
      }
      aria-label={isFirstParse ? "Parse with AI" : "Re-parse with AI"}
      className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200/80 bg-white px-2 py-1 text-[11px] font-semibold text-violet-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-violet-500/30 dark:bg-[oklch(0.26_0.028_264)] dark:text-violet-300 dark:hover:bg-violet-950/40"
    >
      <FontAwesomeIcon
        icon={reparseInFlight ? faCircleNotch : faRotateRight}
        className={reparseInFlight ? "animate-spin text-[9px]" : "text-[9px]"}
        aria-hidden
      />
      <span className="leading-none">
        {reparseInFlight
          ? isFirstParse
            ? "Parsing…"
            : "Re-parsing…"
          : isFirstParse
          ? "Parse with AI"
          : "Re-parse"}
      </span>
    </button>
  ) : null;

  if (iconRailOnly) {
    return (
      <>
        <div
          className="relative z-[1] overflow-hidden rounded-[24px] border border-slate-200 bg-white p-2 shadow-[0_18px_50px_rgba(15,23,42,0.06)] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]"
          aria-label="Shipment inspector"
        >
          <div className="flex flex-col items-center gap-2">
            {onToggleInspectorRail ? (
              <button
                type="button"
                onClick={onToggleInspectorRail}
                title="Expand shipment inspector"
                aria-label="Expand shipment inspector"
                className="flex w-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
              >
                <FontAwesomeIcon icon={faChevronRight} className="text-[11px]" />
              </button>
            ) : null}
            <div className="mt-1 flex w-full flex-col items-center space-y-2">
              {tabItems.map(({ key, label, status }) => {
                const available = isTabAvailable(key);
                const active = activeDetailsTab === key;
                const statusTone = status === "Ready" || status === "Selected";
                const baseIcon = available
                  ? "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264/0.7)]"
                  : "border-slate-200 bg-slate-50 text-slate-500 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.5)] dark:text-slate-600";
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      if (!available) return;
                      setActiveDetailsTab(key);
                      onExpandInspectorFromTab?.();
                    }}
                    title={available ? `${label} — ${status}` : `${label} — not yet applicable`}
                    aria-label={available ? `${label}, ${status}` : `${label}, not available`}
                    aria-current={active ? "true" : undefined}
                    disabled={!available}
                    className={`relative mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-sm transition-all ${
                      active
                        ? "border-fk-accent bg-blue-50 text-fk-accent shadow-[0_8px_20px_rgba(37,99,235,0.12)] dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300"
                        : baseIcon
                    }`}
                  >
                    <FontAwesomeIcon icon={tabIcons[key]} />
                    <span
                      className={`absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full ${
                        statusTone ? "bg-emerald-500" : "bg-amber-500"
                      }`}
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

      </>
    );
  }

  return (
    <>
      <div
        className={`relative z-[1] flex min-h-0 max-h-full flex-1 flex-col overflow-hidden bg-white/58 dark:bg-[oklch(0.225_0.026_264/0.94)] dark:border-[oklch(0.3_0.02_264/0.6)] ${
          compactMode
            ? "rounded-[22px] border border-slate-200/80 shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
            : "rounded-[28px] border border-slate-200 shadow-[0_18px_50px_rgba(15,23,42,0.06)]"
        }`}
      >
        <div className="shrink-0 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/40 px-4 py-3 dark:border-[oklch(0.3_0.02_264/0.6)] dark:from-[#0F1419] dark:to-[#0F1419]/80 sm:px-5">
          <div className="flex flex-col gap-2.5">
            {compactMode ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-fk-accent/10 text-fk-accent dark:bg-blue-500/15 dark:text-blue-300">
                    <FontAwesomeIcon icon={faBox} className="text-[11px]" />
                  </span>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                    Shipment inspector
                  </p>
                  <span className="text-slate-300 dark:text-slate-600" aria-hidden>·</span>
                  <span className="text-[11.5px] text-slate-500 dark:text-slate-400">
                    {fields.packages?.length || 0} pkg{fields.packages?.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {reparseButton}
                  {onToggleInspectorRail ? (
                    <button
                      type="button"
                      onClick={onToggleInspectorRail}
                      title="Collapse shipment inspector to icons"
                      aria-label="Collapse shipment inspector to icons"
                      className="flex min-h-9 min-w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50/70 p-1.5 text-slate-500 transition hover:bg-white hover:text-slate-800 hover:shadow-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="text-[10px]" />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-[16rem] flex-1 items-center gap-2.5">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-fk-accent/15 to-fk-accent/5 text-fk-accent ring-1 ring-fk-accent/20 dark:from-blue-500/20 dark:to-blue-500/5 dark:text-blue-300 dark:ring-blue-500/25">
                    <FontAwesomeIcon icon={faBox} className="text-[14px]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* No duplicated tab-label title here — the tab pills below
                        already name the active section (Container/Customer).
                        min-w on the parent + flex-wrap lets the action buttons
                        drop to their own line before they can crowd this title;
                        truncate is the hard guarantee it can never overflow. */}
                    <h2 className="truncate text-[13px] font-semibold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-200">
                      Shipment inspector
                    </h2>
                    <p className="mt-0.5 truncate text-[11.5px] text-slate-500 dark:text-slate-400">
                      {activeTabMeta.description}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* Deliberately NO Request CTA in the header: the full-width
                      "Request N from customer" button at the foot of the panel
                      fires the identical action (generateMailForMissingFields)
                      on the identical count, so a header copy was a duplicate. */}
                  {reparseButton}
                  {onToggleInspectorRail ? (
                    <button
                      type="button"
                      onClick={onToggleInspectorRail}
                      title="Collapse shipment inspector to icons"
                      aria-label="Collapse shipment inspector to icons"
                      className="flex min-h-9 min-w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50/70 p-1.5 text-slate-500 transition hover:bg-white hover:text-slate-800 hover:shadow-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="text-[10px]" />
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            <div className="-mx-1 overflow-x-auto px-1">
              <div className="flex min-w-max gap-1.5 pb-0.5">
                {tabItems.map(({ key, label, status }) => {
                  const available = isTabAvailable(key);
                  const active = activeDetailsTab === key;
                  const statusTone = status === "Ready" || status === "Selected";
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        if (!available) return;
                        if (String(status).includes("missing")) jumpToMissing(key);
                        else setActiveDetailsTab(key);
                      }}
                      className={`group inline-flex items-center gap-1.5 rounded-xl border px-2 py-1.5 text-left transition ${
                        !available
                          ? "cursor-not-allowed border-slate-200/70 bg-slate-50/60 text-slate-400 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.5)] dark:text-slate-600"
                          : active
                            ? "border-fk-accent/60 bg-blue-50/70 text-slate-950 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.225_0.026_264/0.7)]"
                      }`}
                      role="tab"
                      aria-selected={active}
                      title={
                        !available
                          ? `${label} — not yet applicable`
                          : String(status).includes("missing")
                            ? `${label} — jump to the ${status.split(" ")[0]} missing field${status.startsWith("1 ") ? "" : "s"} (click again for the next)`
                            : `${label} info`
                      }
                      disabled={!available}
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10.5px] ${
                          active
                            ? "bg-white text-fk-accent dark:bg-[oklch(0.168_0.021_264/0.6)] dark:text-blue-300"
                            : "bg-slate-100/80 text-slate-500 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400"
                        }`}
                      >
                        <FontAwesomeIcon icon={tabIcons[key]} />
                      </span>
                      <span className="text-[12px] font-semibold">{label}</span>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.1em] ${
                          statusTone
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                            : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                        }`}
                      >
                        <span
                          className={`inline-block h-1 w-1 rounded-full ${
                            statusTone ? "bg-emerald-500" : "bg-amber-500"
                          }`}
                          aria-hidden
                        />
                        {status}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div className={compactMode ? "bg-white p-3 pb-16 dark:bg-[oklch(0.225_0.026_264)]" : "bg-slate-50/70 p-3 pb-16 dark:bg-[oklch(0.168_0.021_264/0.3)]"}>
          <div data-qd-panel="container" className={activeDetailsTab === "container" ? "block" : "hidden"} role="tabpanel">
            <ContainerDetails
              isEditing={isContainerEditing}
              toggleEditing={handleContainerEditToggle}
              handleFieldChange={handleFieldChange}
              handlePackageChange={handlePackageChange}
              updateContainerEntry={updateContainerEntry}
              updateContainerButtonDisabled={updateContainerButtonDisabled}
              generateMailForMissingFields={generateMailForMissingFields}
              fields={fields}
              setFields={setFields}
              openTrigger={activeDetailsTab}
              compactMode={compactMode}
              hideRequestMissingActions={hideRequestMissingActions}
              parsing={reparseInFlight}
            />
          </div>

          <div data-qd-panel="customer" className={activeDetailsTab === "customer" ? "block" : "hidden"} role="tabpanel">
            <CustomerDetails
              isEditing={isCustomerEditing}
              toggleEditing={() => setIsCustomerEditing(!isCustomerEditing)}
              customer={customer}
              setCustomer={setCustomer}
              updateCustomerEntry={updateCustomerEntry}
              updateCustomerButtonDisabled={updateCustomerButtonDisabled}
              openTrigger={activeDetailsTab}
              compactMode={compactMode}
            />
          </div>

          <div data-qd-panel="shipper" className={activeDetailsTab === "shipper" ? "block" : "hidden"} role="tabpanel">
            <ShipperDetails
              isEditing={isShipperEditing}
              toggleEditing={() => setIsShipperEditing(!isShipperEditing)}
              shipper={shipper}
              handleShipperChange={handleShipperChange}
              updateShipperEntry={updateShipperEntry}
              initialShipper={initialShipper}
              updateShipperButtonDisabled={updateShipperButtonDisabled}
              generateMailForMissingFields={generateMailForMissingFields}
              openTrigger={activeDetailsTab}
              hideRequestMissingActions={hideRequestMissingActions}
            />
          </div>

          <div className={activeDetailsTab === "finalAgent" ? "block" : "hidden"} role="tabpanel">
            <FinalAgentDetails finalAgent={finalAgent} openTrigger={activeDetailsTab} setRefetchTrigger={setRefetchTrigger} />
          </div>

          <div className={activeDetailsTab === "finalAirline" ? "block" : "hidden"} role="tabpanel">
            <FinalAgentDetails finalAgent={finalAirline} type={"airline"} openTrigger={activeDetailsTab} setRefetchTrigger={setRefetchTrigger} />
          </div>
        </div>
        </div>
      </div>

    </>
  );
}
